/**
 * FullscreenModal — a fullscreen overlay variant of react-aria-components
 * Modal. Sibling primitive to `SlideoutMenu`: same compound-component API
 * (`<FullscreenModal.Header/Content/Footer>`) and same single-scroll-container
 * invariant in layout-managed mode, but presents as a full-viewport panel
 * instead of a side panel.
 *
 * Use this for long flows that benefit from maximum horizontal real estate —
 * wizards, multi-section settings panels, on-boarding flows, etc. For shorter
 * focused interactions, prefer SlideoutMenu (side panel) or the standard
 * Modal (centered).
 *
 * Layout modes:
 * - **Layout-managed** (recommended): pass `header` and/or `footer`. Renders
 *   pinned Header, scrollable Content, pinned Footer with the single-scroll
 *   invariant enforced by construction.
 * - **Manual**: omit both — children render directly inside the Dialog. Use
 *   `<FullscreenModal.Content>` to opt into the scrollable region manually.
 */

import type {
  ComponentPropsWithRef,
  CSSProperties,
  ReactNode,
  RefAttributes,
} from 'react';
import { useCallback } from 'react';
import type {
  DialogProps as AriaDialogProps,
  ModalOverlayProps as AriaModalOverlayProps,
  ModalRenderProps as AriaModalRenderProps,
} from 'react-aria-components';
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { CloseButton } from '@/lib/base/buttons/close-button';
import {
  MODAL_OVERLAY_ENTER,
  MODAL_OVERLAY_EXIT,
} from '@/lib/base/overlays/modal-animation';
import { cx } from '@/utils/cx';

// =============================================================================
// Sub-primitives (overlay, modal frame, dialog)
// =============================================================================

interface ModalOverlayProps
  extends AriaModalOverlayProps, RefAttributes<HTMLDivElement> {}

export const ModalOverlay = (props: ModalOverlayProps) => {
  const zIndex = useStackingZIndex();
  const { className: propsClassName, style: propsStyle } = props;
  const getClassName = useCallback(
    (state: AriaModalRenderProps & { defaultClassName: string | undefined }) =>
      cx(
        'fixed inset-0 bg-primary outline-hidden',
        state.isEntering && MODAL_OVERLAY_ENTER,
        state.isExiting && MODAL_OVERLAY_EXIT,
        typeof propsClassName === 'function'
          ? propsClassName(state)
          : propsClassName
      ),
    [propsClassName]
  );
  // Merge caller-provided style with our zIndex so consumers can pass their
  // own inline style without losing the stacking-context wiring.
  const getStyle = useCallback(
    (
      state: AriaModalRenderProps & { defaultStyle: CSSProperties }
    ): CSSProperties => ({
      ...(typeof propsStyle === 'function' ? propsStyle(state) : propsStyle),
      zIndex,
    }),
    [propsStyle, zIndex]
  );
  return (
    <AriaModalOverlay {...props} className={getClassName} style={getStyle} />
  );
};
ModalOverlay.displayName = 'FullscreenModalOverlay';

interface ModalProps
  extends AriaModalOverlayProps, RefAttributes<HTMLDivElement> {}

export const Modal = (props: ModalProps) => {
  const zIndex = useStackingZIndex(10);
  const { className: propsClassName, style: propsStyle } = props;
  const getClassName = useCallback(
    (state: AriaModalRenderProps & { defaultClassName: string | undefined }) =>
      cx(
        // Use dynamic viewport height via the --app-viewport-height custom
        // property (set by useViewportHeight from window.visualViewport) so
        // the modal stays inside the visible area on mobile browsers, where
        // 100vh under-/over-shoots once the URL bar collapses. Fall back to
        // 100dvh for browsers without the custom property set.
        //
        // Width is w-full, NOT w-screen: the overlay parent is `fixed
        // inset-0`, so w-full is exactly the visible viewport. w-screen
        // (100vw) includes the vertical scrollbar's width whenever the
        // underlying page shows one (Windows, macOS "always show
        // scrollbars"), pushing the modal's right edge off-screen and
        // clipping content pinned to it.
        'h-[var(--app-viewport-height,100dvh)] w-full',
        typeof propsClassName === 'function'
          ? propsClassName(state)
          : propsClassName
      ),
    [propsClassName]
  );
  // Merge caller-provided style with our zIndex so consumers can pass their
  // own inline style without losing the stacking-context wiring.
  const getStyle = useCallback(
    (
      state: AriaModalRenderProps & { defaultStyle: CSSProperties }
    ): CSSProperties => ({
      ...(typeof propsStyle === 'function' ? propsStyle(state) : propsStyle),
      zIndex,
    }),
    [propsStyle, zIndex]
  );
  return <AriaModal {...props} className={getClassName} style={getStyle} />;
};
Modal.displayName = 'FullscreenModal';

interface DialogProps
  extends Omit<AriaDialogProps, 'children'>, RefAttributes<HTMLElement> {
  children?: ReactNode | ((opts: { close: () => void }) => ReactNode);
}

export const Dialog = ({ children, ...props }: DialogProps) => (
  <AriaDialog
    role='dialog'
    aria-label='Fullscreen panel'
    {...props}
    className={cx(
      // Outer panel must NOT scroll. Content owns the scrollbar so Header
      // and Footer stay pinned. Matches SlideoutMenu's single-scroll-
      // container invariant.
      'relative flex h-full w-full flex-col overflow-hidden outline-none',
      props.className
    )}
  >
    {({ close }) => (
      <StackingLayer>
        {typeof children === 'function' ? children({ close }) : children}
      </StackingLayer>
    )}
  </AriaDialog>
);
Dialog.displayName = 'FullscreenDialog';

// =============================================================================
// Layout-managed body (mirrors SlideoutMenu's pattern)
// =============================================================================

interface FullscreenModalProps
  extends
    Omit<AriaModalOverlayProps, 'children'>,
    RefAttributes<HTMLDivElement> {
  children:
    | ReactNode
    | ((children: AriaModalRenderProps & { close: () => void }) => ReactNode);
  dialogClassName?: string;
  /**
   * Accessible label for the inner Dialog. Defaults to "Fullscreen panel".
   * Set this to something specific (e.g. "Create object type", "Import CSV")
   * so screen readers announce the right context when the modal opens.
   */
  dialogAriaLabel?: string;
  /**
   * Optional header content. When provided, the modal switches to
   * layout-managed mode: header is rendered in a pinned <Header> slot at
   * the top with a built-in close button, `children` is auto-wrapped in
   * a scrollable <Content> region, and `footer` (if provided) is pinned
   * at the bottom.
   *
   * Omit `header` AND `footer` to fall back to manual mode (children
   * render directly inside the Dialog with no auto-layout). Consumers in
   * manual mode MUST wrap any potentially-overflowing content in
   * `<FullscreenModal.Content>` (or another `overflow-y-auto` region) —
   * the Dialog wrapper is `overflow-hidden`.
   */
  header?: ReactNode;
  /**
   * Optional footer content (typically action buttons). See `header`
   * docs for layout-managed mode semantics. Either prop opts in.
   */
  footer?: ReactNode;
  /** Optional className applied to the auto-wrapped Content scroll region. */
  contentClassName?: string;
  /**
   * Optional className applied to the pinned Header slot in layout-managed
   * mode. Merged after the defaults, so consumers can e.g. tighten the
   * vertical padding (`py-2.5`) for toolbar-dense headers.
   */
  headerClassName?: string;
  /**
   * Max width (px) of the centered content column in layout-managed mode.
   * Applied to the header, body, and footer alike so they stay visually
   * aligned — otherwise footer actions strand themselves in the far corner
   * on wide displays while the body stays centered.
   *
   * The modal itself is deliberately full-bleed; this constrains only the
   * content inside it. Pass a wider value (e.g. `1280`) for content that
   * benefits from horizontal space, such as data tables.
   *
   * Pass `'none'` to opt out entirely when the consumer centers its own
   * content — `Wizard` does this, since it owns a per-step `maxContentWidth`.
   *
   * Default: 672 (matches Tailwind's `max-w-2xl` and the Wizard default).
   */
  maxContentWidth?: number | 'none';
  /** Optional test-handle instance, surfaced as data-instance on the rendered Dialog. */
  instance?: string;
  /**
   * Hide the built-in close button in the layout-managed Header. Consumers
   * who pass custom header content with their own close affordance can opt
   * out so the chrome doesn't double up. Default: false.
   */
  hideCloseButton?: boolean;
  /**
   * Extra className for the built-in Header close button. Useful for
   * responsive hiding (e.g. `hidden md:flex` to drop the close button on
   * mobile in favour of a footer Cancel action).
   */
  closeButtonClassName?: string;
  /**
   * Size of the built-in Header close button. Defaults to `md` (40px). Pass a
   * smaller size when `headerClassName` tightens the header into a short band,
   * so the close doesn't outweigh the actions beside it.
   */
  closeButtonSize?: 'xs' | 'sm' | 'md' | 'lg';
}

// A ReactNode counts as "provided" for layout-managed opt-in only when it
// could actually render content. `null`, `undefined`, booleans, and empty
// strings (the values React renders as nothing) do NOT count — otherwise a
// conditional like `header={cond && <Foo />}` or `header={label}` (where
// `label` happens to be `""`) would silently flip into layout-managed mode
// and render empty Header chrome with a close button.
function isRenderableNode(node: ReactNode): boolean {
  if (node === undefined || node === null || typeof node === 'boolean') {
    return false;
  }
  if (typeof node === 'string') {
    return node.length > 0;
  }
  return true;
}

function resolveChildren(
  children: FullscreenModalProps['children'],
  state: AriaModalRenderProps,
  close: () => void
): ReactNode {
  return typeof children === 'function'
    ? children({ ...state, close })
    : children;
}

/**
 * Default centered-content width (px) for layout-managed mode. Matches
 * Tailwind's `max-w-2xl` and the Wizard's own default, so a modal that
 * doesn't opt in still reads as a centered column rather than stretching
 * edge-to-edge on a wide display.
 */
const DEFAULT_MAX_CONTENT_WIDTH = 672;

interface BodyProps {
  header: ReactNode;
  footer: ReactNode;
  contentClassName: string | undefined;
  headerClassName: string | undefined;
  maxContentWidth: number | 'none';
  hideCloseButton: boolean;
  closeButtonClassName: string | undefined;
  closeButtonSize: FullscreenModalProps['closeButtonSize'];
  children: FullscreenModalProps['children'];
  state: AriaModalRenderProps;
  close: () => void;
}

function Body({
  header,
  footer,
  contentClassName,
  headerClassName,
  maxContentWidth,
  hideCloseButton,
  closeButtonClassName,
  closeButtonSize,
  children,
  state,
  close,
}: BodyProps) {
  // `'none'` means the consumer centers its own content (see Wizard), so the
  // slots render unwrapped exactly as before.
  const centerContent = (node: ReactNode) =>
    maxContentWidth === 'none' ? (
      node
    ) : (
      <div className='mx-auto w-full' style={{ maxWidth: maxContentWidth }}>
        {node}
      </div>
    );

  return (
    <>
      {isRenderableNode(header) && (
        <Header
          onClose={hideCloseButton ? undefined : close}
          closeButtonClassName={closeButtonClassName}
          closeButtonSize={closeButtonSize}
          className={headerClassName}
        >
          {centerContent(header)}
        </Header>
      )}
      <Content className={contentClassName}>
        {centerContent(resolveChildren(children, state, close))}
      </Content>
      {isRenderableNode(footer) && <Footer>{centerContent(footer)}</Footer>}
    </>
  );
}
Body.displayName = 'FullscreenModalBody';

// =============================================================================
// Main component
// =============================================================================

const FullscreenModalRoot = ({
  children,
  dialogClassName,
  dialogAriaLabel,
  isDismissable = false,
  isKeyboardDismissDisabled = true,
  header,
  footer,
  contentClassName,
  headerClassName,
  maxContentWidth = DEFAULT_MAX_CONTENT_WIDTH,
  instance,
  hideCloseButton = false,
  closeButtonClassName,
  closeButtonSize,
  ...props
}: FullscreenModalProps) => {
  // Layout-managed mode: opted in by passing a renderable `header` and/or
  // `footer`. The modal owns the Header → Content → Footer composition
  // so the single-scroll-container invariant cannot be broken by consumers.
  const isLayoutManaged = isRenderableNode(header) || isRenderableNode(footer);

  return (
    <ModalOverlay
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      {...props}
    >
      <Modal
        className={state =>
          cx(
            typeof props.className === 'function'
              ? props.className(state)
              : props.className
          )
        }
      >
        {state => (
          <Dialog
            className={cx('bg-primary', dialogClassName)}
            {...(dialogAriaLabel?.trim()
              ? { 'aria-label': dialogAriaLabel }
              : {})}
            data-component='fullscreen-modal'
            data-instance={instance}
          >
            {({ close }) => (
              <>
                {/* Hidden marker for E2E tests to detect transition completion */}
                <div
                  data-testid='fullscreen-modal-ready-marker'
                  data-component='fullscreen-modal-ready-marker'
                  aria-hidden='true'
                  style={{ display: 'none' }}
                  data-ready={
                    !state.isEntering && !state.isExiting ? '' : undefined
                  }
                />
                {isLayoutManaged ? (
                  <Body
                    header={header}
                    footer={footer}
                    maxContentWidth={maxContentWidth}
                    contentClassName={contentClassName}
                    headerClassName={headerClassName}
                    hideCloseButton={hideCloseButton}
                    closeButtonClassName={closeButtonClassName}
                    closeButtonSize={closeButtonSize}
                    state={state}
                    close={close}
                  >
                    {children}
                  </Body>
                ) : (
                  resolveChildren(children, state, close)
                )}
              </>
            )}
          </Dialog>
        )}
      </Modal>
    </ModalOverlay>
  );
};
FullscreenModalRoot.displayName = 'FullscreenModal';

// =============================================================================
// Compound subcomponents
// =============================================================================

const Content = (props: ComponentPropsWithRef<'div'>) => {
  return (
    <div
      {...props}
      className={cx(
        'flex min-h-0 w-full flex-1 flex-col overflow-y-auto overscroll-auto',
        props.className
      )}
    />
  );
};
Content.displayName = 'FullscreenModalContent';

interface HeaderProps extends ComponentPropsWithRef<'header'> {
  /**
   * Close handler — when provided, renders the built-in close button. Omit
   * to render the header without a close button (e.g. if the consumer
   * supplies their own close affordance).
   */
  onClose?: () => void;
  /** Extra className for the close button (e.g. responsive hiding). */
  closeButtonClassName?: string;
  /**
   * Size of the built-in close button. Defaults to `md` (40px), which suits
   * the default `py-4` header (68px tall).
   *
   * A header given a shorter fixed band needs a smaller one: at 40px inside a
   * 44px band the close fills the bar edge to edge and visibly outweighs the
   * actions beside it. Match it to the other controls in the band instead —
   * `xs` is 28px, `sm` 36px.
   */
  closeButtonSize?: 'xs' | 'sm' | 'md' | 'lg';
}

const Header = ({
  className,
  children,
  onClose,
  closeButtonClassName,
  closeButtonSize = 'md',
  ...props
}: HeaderProps) => {
  return (
    <header
      {...props}
      className={cx(
        'relative z-1 w-full shrink-0 border-b border-border-secondary px-4 py-4 md:px-6',
        className
      )}
    >
      {/* When the close button is present, reserve right-side space so header
          content (titles, step indicators) never sits underneath it.

          `min-w-0 flex-1` is inert while this header is the default block
          layout (flex properties only apply to flex items), and is what lets a
          consumer pass `flex items-center` to centre header content: without
          it this wrapper becomes a shrink-to-fit flex item and collapses any
          `justify-between` inside it. Centring matters because `py-4` is not
          the only way this header is used — given a `min-h` band taller than
          its padding plus content, block layout puts ALL the slack at the
          bottom and the content rides the top edge. */}
      {onClose ? (
        <div className='min-w-0 flex-1 pr-12 md:pr-14'>{children}</div>
      ) : (
        children
      )}
      {onClose && (
        <CloseButton
          size={closeButtonSize}
          className={cx(
            'absolute top-1/2 right-3 -translate-y-1/2 shrink-0',
            closeButtonClassName
          )}
          onClick={onClose}
        />
      )}
    </header>
  );
};
Header.displayName = 'FullscreenModalHeader';

const Footer = (props: ComponentPropsWithRef<'footer'>) => {
  return (
    <footer
      {...props}
      className={cx(
        'w-full shrink-0 border-t border-border-secondary px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:px-6',
        props.className
      )}
    />
  );
};
Footer.displayName = 'FullscreenModalFooter';

// =============================================================================
// Public export (compound component pattern)
// =============================================================================

const FullscreenModal = FullscreenModalRoot as typeof FullscreenModalRoot & {
  Trigger: typeof AriaDialogTrigger;
  Content: typeof Content;
  Header: typeof Header;
  Footer: typeof Footer;
};
FullscreenModal.displayName = 'FullscreenModal';

FullscreenModal.Trigger = AriaDialogTrigger;
FullscreenModal.Content = Content;
FullscreenModal.Header = Header;
FullscreenModal.Footer = Footer;

export { FullscreenModal };
